import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@client/components/hooks/use-toast';
import {
  selectSessionAdvisor,
  selectPendingSeatInvitations,
  selectSeatInvitationAcceptResponse,
  selectSeatInvitationDeclineResponse,
} from '@client/model/selectors';
import {
  postSeatInvitationAcceptBySubscriptionSeatIdRequest,
  postSeatInvitationDeclineBySubscriptionSeatIdRequest,
  fetchSubscriptionSeatsByAdvisorIdRequest,
} from '@client/model/subscriptionSeatsModel';
import { subscriptionSeatsKeys } from '@client/api/waterlily';

interface ConsentData {
  hasConsented: boolean;
  ipAddress: string | null;
  userAgent: string;
}

interface UseSeatInvitationAcceptDeclineOptions {
  advisorId?: string;
  onAcceptSuccess?: () => void;
  onDeclineSuccess?: () => void;
}

/**
 * Hook for managing accept/decline actions on pending seat invitations
 * from the Subscription page.
 *
 * This hook handles:
 * - Fetching pending invitations from the Redux store
 * - Managing accept/decline dialog state
 * - Dispatching accept/decline requests with consent data
 * - Showing success/error toast notifications
 * - Auto-refetching seats on success (Redux)
 * - Invalidating React Query cache for onboarding flows
 * - Calling optional success callbacks for navigation
 */
export function useSeatInvitationAcceptDecline(options?: UseSeatInvitationAcceptDeclineOptions) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { advisorId, onAcceptSuccess, onDeclineSuccess } = options ?? {};

  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const pendingSeatInvitations = useSelector(selectPendingSeatInvitations);
  const acceptResponse = useSelector(selectSeatInvitationAcceptResponse);
  const declineResponse = useSelector(selectSeatInvitationDeclineResponse);

  // Dialog state for accept (with consent)
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [invitationToAccept, setInvitationToAccept] = useState<PendingSeatInvitation | null>(null);

  // Dialog state for decline confirmation
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [invitationToDecline, setInvitationToDecline] = useState<PendingSeatInvitation | null>(
    null,
  );

  // Track previous mutation states for completion detection
  const prevAcceptingRef = useRef(false);
  const prevDecliningRef = useRef(false);

  const isAccepting = acceptResponse.status === 'loading';
  const isDeclining = declineResponse.status === 'loading';
  const isProcessing = isAccepting || isDeclining;

  /**
   * Handles post-mutation action to refetch subscription seats.
   */
  const handlePostMutationAction = useCallback(() => {
    if (sessionAdvisor?.advisorId) {
      dispatch(fetchSubscriptionSeatsByAdvisorIdRequest(sessionAdvisor.advisorId));
    }
    // Invalidate React Query cache for onboarding flows
    if (advisorId) {
      queryClient.invalidateQueries({
        queryKey: subscriptionSeatsKeys.byAdvisor(advisorId),
      });
    }
  }, [sessionAdvisor?.advisorId, advisorId, dispatch, queryClient]);

  // Handle accept mutation completion
  useEffect(() => {
    if (prevAcceptingRef.current && !isAccepting) {
      if (acceptResponse.status === 'error') {
        toast({
          title: 'Error',
          description: acceptResponse.error || 'Failed to accept invitation. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else if (acceptResponse.status === 'success') {
        toast({
          title: 'Invitation Accepted',
          description: acceptResponse.message || 'Your subscription seat has been activated.',
          className: '-translate-y-5',
        });

        setAcceptDialogOpen(false);
        setInvitationToAccept(null);
        handlePostMutationAction();
        // Call optional success callback for navigation (onboarding redirect to next step)
        onAcceptSuccess?.();
      }
    }

    prevAcceptingRef.current = isAccepting;
  }, [
    isAccepting,
    acceptResponse.status,
    acceptResponse.error,
    acceptResponse.message,
    handlePostMutationAction,
    onAcceptSuccess,
    toast,
  ]);

  // Handle decline mutation completion
  useEffect(() => {
    if (prevDecliningRef.current && !isDeclining) {
      if (declineResponse.status === 'error') {
        toast({
          title: 'Error',
          description: declineResponse.error || 'Failed to decline invitation. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else if (declineResponse.status === 'success') {
        toast({
          title: 'Invitation Declined',
          description: declineResponse.message || 'The invitation has been declined.',
          className: '-translate-y-5',
        });

        setDeclineDialogOpen(false);
        setInvitationToDecline(null);
        handlePostMutationAction();
        // Call optional success callback (for UI reset)
        onDeclineSuccess?.();
      }
    }

    prevDecliningRef.current = isDeclining;
  }, [
    isDeclining,
    declineResponse.status,
    declineResponse.error,
    declineResponse.message,
    handlePostMutationAction,
    onDeclineSuccess,
    toast,
  ]);

  /**
   * Opens the accept dialog for a specific invitation
   */
  const handleAccept = useCallback((invitation: PendingSeatInvitation) => {
    setInvitationToAccept(invitation);
    setAcceptDialogOpen(true);
  }, []);

  /**
   * Confirms acceptance with consent data
   */
  const confirmAccept = useCallback(
    (consentData: ConsentData) => {
      if (!invitationToAccept) return;

      dispatch(
        postSeatInvitationAcceptBySubscriptionSeatIdRequest({
          subscriptionSeatId: invitationToAccept.subscriptionSeatId,
          ipAddress: consentData.ipAddress,
          userAgent: consentData.userAgent,
          metadata: { consentCapturedAt: new Date().toISOString() },
        }),
      );
    },
    [dispatch, invitationToAccept],
  );

  /**
   * Cancels the accept dialog
   */
  const cancelAccept = useCallback(() => {
    setAcceptDialogOpen(false);
    setInvitationToAccept(null);
  }, []);

  /**
   * Opens the decline confirmation dialog for a specific invitation
   */
  const handleDecline = useCallback((invitation: PendingSeatInvitation) => {
    setInvitationToDecline(invitation);
    setDeclineDialogOpen(true);
  }, []);

  /**
   * Confirms decline - keeps dialog open while processing
   */
  const confirmDecline = useCallback(() => {
    if (!invitationToDecline) return;

    dispatch(
      postSeatInvitationDeclineBySubscriptionSeatIdRequest({
        subscriptionSeatId: invitationToDecline.subscriptionSeatId,
      }),
    );
    // Dialog will be closed by the useEffect when decline completes
  }, [dispatch, invitationToDecline]);

  /**
   * Cancels the decline dialog
   */
  const cancelDecline = useCallback(() => {
    setDeclineDialogOpen(false);
    setInvitationToDecline(null);
  }, []);

  return {
    // Data
    pendingSeatInvitations,

    // Accept dialog state
    acceptDialogOpen,
    invitationToAccept,

    // Decline dialog state
    declineDialogOpen,
    invitationToDecline,

    // Mutation state
    isAccepting,
    isDeclining,
    isProcessing,

    // Actions
    handleAccept,
    confirmAccept,
    cancelAccept,
    handleDecline,
    confirmDecline,
    cancelDecline,
  };
}

export type UseSeatInvitationAcceptDeclineReturn = ReturnType<
  typeof useSeatInvitationAcceptDecline
>;
